import CartInertiaController from '@/actions/App/Http/Controllers/CartInertiaController';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Form } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { Typo } from '@/components/ui/typography';
import { useIsMobile } from '@/hooks/use-mobile';
import { UserCartItem, useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { useLoginGate } from '@/contexts/login-gate-provider';
import { formatCurrency } from '@/lib/accounting';
import { get, post, toErrorMessage } from '@/lib/axios';
import { clearPendingCartAction, executeCartAction, savePendingCartAction, type PendingCartAction } from '@/lib/pending-cart-action';
import { type SharedData } from '@/types';
import { type BookingRate } from '@/types/search';
import { usePage } from '@inertiajs/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Minus, Plus, ShoppingCart, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

// Zod schema for form validation using mapping codes
const cartFormSchema = z.object({
  rate: z.string().min(1, 'Vui lòng chọn gói dịch vụ'),
  site: z.string().min(1, 'Vui lòng chọn địa điểm'),
  date: z.string().min(1, 'Vui lòng chọn ngày'),
  services: z
    .array(
      z.object({
        service_mapping_code: z.string().min(1, 'Vui lòng chọn dịch vụ'),
        service_name: z.string(),
        quantity: z.number().min(1, 'Vui lòng chọn số lượng').max(500, 'Số lượng tối đa là 500'),
      }),
    )
    .min(0, 'Vui lòng chọn ít nhất một dịch vụ')
    .refine((services) => services.length > 0 && services.some((service) => service.quantity > 0), { message: 'Vui lòng chọn ít nhất một dịch vụ' }),
});

type CartFormData = z.infer<typeof cartFormSchema>;

interface ServiceCartDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rate?: BookingRate | null;
  cartItem?: UserCartItem;
  date?: string;
  searchParams: {
    site: string; // This should be the mapping code
    date: string;
  };
}

export function ServiceCartDrawer({ open, onOpenChange, rate, searchParams, cartItem }: ServiceCartDrawerProps) {
  const isMobile = useIsMobile();
  const { cart } = useUserGlobalCart();
  const { auth } = usePage<SharedData>().props;
  const user = auth?.user ?? null;
  const { requireLogin } = useLoginGate();
  const [fomattedDate, setFomattedDate] = useState(searchParams.date);
  const [fetchingRateDetail, setFetchingRateDetail] = useState(false);
  const [rateDetail, setRateDetail] = useState<BookingRate | null>(null);
  const [submittingMode, setSubmittingMode] = useState<'add' | 'buyNow' | null>(null);

  // Initialize form with React Hook Form + Zod
  const form = useForm<CartFormData>({
    resolver: zodResolver(cartFormSchema),
    defaultValues: {
      rate: rate?.Code || cartItem?.options?.rate_mapping_code,
      site: searchParams.site,
      date: searchParams.date,
      services: [],
    },
  });

  const { append, remove, update } = useFieldArray({
    control: form.control,
    name: 'services',
  });

  const watchedServices = form.watch('services');

  // Check if this rate already exists in cart
  const existingCartItems = useMemo(() => {
    if (!rate && !cartItem) return null;
    let rateMappingCode = '';
    if (rate) {
      rateMappingCode = rate.Code;
    }
    if (cartItem) rateMappingCode = cartItem.options?.rate_mapping_code || '';
    const cartItems = cart.filter(
      (item) =>
        item.options?.rate_mapping_code === rateMappingCode &&
        item.options?.site_mapping_code === searchParams.site &&
        item.options?.date === searchParams.date,
    );
    return cartItems.length > 0 ? cartItems : null;
  }, [cart, rate, searchParams, cartItem]);

  // Helper functions for managing services in the form
  const findServiceInForm = (serviceMappingCode: string) => {
    return watchedServices.findIndex((item) => item.service_mapping_code === serviceMappingCode);
  };

  const getServiceQuantity = (serviceMappingCode: string) => {
    const serviceIndex = findServiceInForm(serviceMappingCode);
    return serviceIndex >= 0 ? watchedServices[serviceIndex].quantity : 0;
  };

  const updateServiceQuantity = (serviceMappingCode: string, quantity: number, serviceName: string) => {
    const serviceIndex = findServiceInForm(serviceMappingCode);

    if (quantity === 0) {
      // Remove service from form when quantity is 0
      if (serviceIndex >= 0) {
        remove(serviceIndex);
      }
    } else {
      const service = {
        service_mapping_code: serviceMappingCode,
        service_name: serviceName,
        quantity,
      };
      if (serviceIndex >= 0) {
        // Update existing service
        update(serviceIndex, service);
      } else {
        // Add new service to form
        append(service);
      }
    }
  };

  // Initialize form when rate changes
  useEffect(() => {
    if (open && (rate || cartItem)) {
      const rateMappingCode = rate?.Code || cartItem?.options?.rate_mapping_code;

      // Initialize services with existing cart quantities if available
      const initialServices: Array<{
        service_mapping_code: string;
        service_name: string;
        quantity: number;
      }> = [];

      if (existingCartItems) {
        // Pre-populate with current cart quantities
        existingCartItems.forEach((cartItem) => {
          const serviceMappingCode = cartItem.options?.service_mapping_code;
          if (serviceMappingCode) {
            const existingService = initialServices.find((s) => s.service_mapping_code === serviceMappingCode);
            if (existingService) {
              existingService.quantity += cartItem.qty;
            } else {
              initialServices.push({
                service_mapping_code: serviceMappingCode,
                service_name: cartItem.name,
                quantity: cartItem.qty,
              });
            }
          }
        });
      }

      form.reset({
        rate: rateMappingCode,
        site: searchParams.site,
        date: searchParams.date,
        services: initialServices,
      });
    }
  }, [rate, searchParams, form, open, existingCartItems]);

  const getGrandTotal = () => {
    if (!rate && !cartItem) return 0;
    if (rate) {
      return watchedServices.reduce((total, serviceData) => {
        const rateService =
          rate?.Services.find((service) => (service.MappingCode || service.Code) === serviceData.service_mapping_code) ||
          cartItem?.options?.services?.find((service) => service.service_mapping_code === serviceData.service_mapping_code);
        return total + (rateService ? serviceData.quantity * rateService.Price : 0);
      }, 0);
    } else if (cartItem) {
      return watchedServices.reduce((total, serviceData) => {
        const rateService = cartItem?.options?.services?.find((service) => service.service_mapping_code === serviceData.service_mapping_code);
        return total + (rateService ? serviceData.quantity * rateService.Price : 0);
      }, 0);
    }
    return 0;
  };

  useEffect(() => {
    if (!open) return;
    const fetchRateWithServices = async () => {
      setFetchingRateDetail(true);

      try {
        const siteMappingCode = cartItem?.options?.site_mapping_code || searchParams.site;
        const rateMappingCode = rate?.Code || cartItem?.options?.rate_mapping_code;
        const itemDate = fomattedDate;

        if (!rateMappingCode || !itemDate) {
          toast.error('Không thể tải thông tin dịch vụ');
          return;
        }

        // Fetch rate details with site and date to get API-validated available services
        const response = await get<{ success: boolean; data: BookingRate }>(`/cart/rate-with-services`, {
          params: {
            site: siteMappingCode,
            rate: rateMappingCode,
            date: itemDate,
            with_site: true,
          },
        });

        if (response.success) {
          setRateDetail(response.data as any);
        } else {
          toast.error('Không tìm thấy thông tin gói dịch vụ');
        }
      } catch (error) {
        console.error('Error fetching rate data:', error);
        toast.error('Có lỗi xảy ra khi tải thông tin dịch vụ');
      } finally {
        setFetchingRateDetail(false);
      }
    };

    if ((cartItem || rate) && fomattedDate) {
      fetchRateWithServices();
    }
  }, [fomattedDate, cartItem, rate, searchParams, open]);

  // Update flow for items already in the cart (same rate + site + date)
  const submitUpdate = async (data: CartFormData) => {
    try {
      const response = await post(CartInertiaController.rateAdd().url, data);

      if (response.success) {
        toast.success('Đã cập nhật giỏ hàng');
        onOpenChange(false);

        // Trigger cart update event for other components
        window.dispatchEvent(new CustomEvent('cart-updated'));
      } else {
        toast.error(toErrorMessage(response.message, 'Có lỗi xảy ra khi cập nhật giỏ hàng'));
      }
    } catch (error: any) {
      console.error('Cart update error:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi cập nhật giỏ hàng');
    }
  };

  // Add / buy-now flow — executeCartAction handles toast, cart-updated event and navigation
  const submitCartAction = async (mode: 'add' | 'buyNow', data: CartFormData) => {
    const action: PendingCartAction = { type: mode, payload: data };

    if (user) {
      setSubmittingMode(mode);
      try {
        const success = await executeCartAction(action);
        if (success) {
          onOpenChange(false);
        }
      } finally {
        setSubmittingMode(null);
      }
      return;
    }

    // Guest: persist the action so a social login (full page redirect) can resume it;
    // the password-login path resumes through the requireLogin callback instead.
    savePendingCartAction(action);
    requireLogin(async () => {
      clearPendingCartAction();
      const success = await executeCartAction(action);
      if (success) {
        onOpenChange(false);
      }
    });
  };

  const onSubmit = async (data: CartFormData, mode: 'add' | 'buyNow' = 'add') => {
    // Services are already filtered - only selected services are in the array
    if (data.services.length === 0) {
      toast.error('Vui lòng chọn ít nhất một dịch vụ');
      return;
    }

    if (existingCartItems) {
      if (user) {
        await submitUpdate(data);
      } else {
        requireLogin(() => submitUpdate(data));
      }
      return;
    }

    await submitCartAction(mode, data);
  };

  const handleModeSubmit = (mode: 'add' | 'buyNow') => {
    return form.handleSubmit((data) => onSubmit(data, mode))();
  };

  const hasSelectedServices = () => {
    return watchedServices.length > 0;
  };

  const isBusy = form.formState.isSubmitting || submittingMode !== null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction={isMobile ? 'bottom' : 'right'}>
      <DrawerContent className="z-[52] h-full overflow-hidden rounded-l-3xl p-4 data-[vaul-drawer-direction=right]:sm:max-w-3xl md:px-8 md:py-8">
        <DrawerHeader className="mb-4 space-y-2 p-0 pt-4">
          {!fetchingRateDetail && rateDetail ? (
            <>
              <DrawerTitle className="text-left text-2xl">
                [{rateDetail?.SiteName}] {rateDetail?.DisplayName || rateDetail?.Name}
              </DrawerTitle>
              <Typo variant="muted" className="text-left">
                {rateDetail?.TypeName}
              </Typo>

              {existingCartItems && (
                <Typo variant="small" className="text-primary mt-1 text-left">
                  Dịch vụ này đã có trong giỏ hàng - cập nhật số lượng
                </Typo>
              )}
            </>
          ) : (
            <>
              <DrawerTitle />
              <HeaderSkeleton />
            </>
          )}
        </DrawerHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => onSubmit(data, 'add'))} className="space-y-6 overflow-x-hidden pr-1">
            {/* Services list */}
            <div className="space-y-4">
              {!fetchingRateDetail && rateDetail?.Services && rateDetail?.Services?.length
                ? rateDetail?.Services.map((service) => (
                    <div key={service.Code} className="rounded-2xl border p-4">
                      <div className="flex items-center justify-between">
                        <div className="col-span-2 mb-3 flex flex-col gap-1">
                          <Typo variant="h6" className="font-medium">
                            {service.DisplayName || service.Name}{' '}
                            {service.MinTicketBooking > 1 && (
                              <span className="inline-block text-xs text-red-400">(Tối thiểu {service.MinTicketBooking} vé)</span>
                            )}
                          </Typo>
                          <Typo variant="muted">{service.TypeName}</Typo>
                          <Typo className="text-md text-primary !mt-0 font-medium">{formatCurrency(service.Price, 'vnđ')}</Typo>
                        </div>

                        {/* Quantity selector */}
                        <div className="flex items-center justify-end" data-service-key={`${service.Code}_${rateDetail?.Code}_${fomattedDate}`}>
                          <Counter
                            label=""
                            description=""
                            value={getServiceQuantity(service.Code)}
                            onChange={(value) => updateServiceQuantity(service.Code, value, service.DisplayName || service.Name)}
                            min={0}
                            minTicketBooking={service.MinTicketBooking}
                            icon={<Users className="h-4 w-4" />}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                : null}

              {fetchingRateDetail ? <RateServicesSkeleton /> : null}
            </div>

            {/* Total and submit */}

            {fetchingRateDetail ? (
              <ActionsSkeleton />
            ) : (
              <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t bg-white pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">Tổng cộng:</span>
                  <span className="text-primary text-lg font-semibold">{formatCurrency(getGrandTotal())}</span>
                </div>

                {existingCartItems ? (
                  <Button type="submit" size="lg" className="rounded-full" disabled={!hasSelectedServices() || isBusy}>
                    <ShoppingCart className="size-4" />
                    {form.formState.isSubmitting ? 'Đang cập nhật...' : 'Cập nhật giỏ hàng'}
                  </Button>
                ) : (
                  <div className="flex flex-1 justify-end gap-2 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      size="lg"
                      className="rounded-full"
                      disabled={!hasSelectedServices() || isBusy}
                      onClick={() => handleModeSubmit('add')}
                    >
                      <ShoppingCart className="size-4" />
                      {submittingMode === 'add' ? 'Đang thêm...' : 'Thêm vào giỏ hàng'}
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      className="rounded-full"
                      disabled={!hasSelectedServices() || isBusy}
                      onClick={() => handleModeSubmit('buyNow')}
                    >
                      {submittingMode === 'buyNow' ? 'Đang xử lý...' : 'Mua ngay'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </form>
        </Form>
        <Button variant="outline" className="absolute top-4 right-4 size-8 rounded-lg" size="icon" onClick={() => onOpenChange(false)}>
          <X className="h-4 w-4" />
        </Button>
      </DrawerContent>
    </Drawer>
  );
}

interface CounterProps {
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  minTicketBooking?: number;
  icon?: React.ReactNode;
}

function Counter({ label, description, value, onChange, min = 0, max = 500, minTicketBooking = 1, icon }: CounterProps) {
  const [inputValue, setInputValue] = useState(value.toString());

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;

    // Allow empty input for user convenience while typing
    if (inputVal === '') {
      setInputValue('');
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(inputVal)) {
      return;
    }

    setInputValue(inputVal);
  };

  const handleInputBlur = () => {
    let numValue = parseInt(inputValue) || 0;

    // Apply min/max constraints
    numValue = Math.max(min, Math.min(max, numValue));

    // Handle minTicketBooking logic: if value is between 1 and minTicketBooking-1, adjust to minTicketBooking
    if (numValue > 0 && numValue < minTicketBooking) {
      numValue = minTicketBooking;
    }

    setInputValue(numValue.toString());
    onChange(numValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Enter to trigger blur (finalize input)
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
    // Block non-numeric keys except for navigation and control keys
    if (!/[\d]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-start gap-3">
        {icon && <div className="text-primary mt-1">{icon}</div>}
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-muted-foreground text-sm">{description}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          disabled={value <= min}
          onClick={() => {
            if (value > 0 && value <= minTicketBooking) {
              // If decreasing from minTicketBooking or below, go to 0
              onChange(0);
            } else {
              onChange(Math.max(min, value - 1));
            }
          }}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className="border-input bg-background text-md focus:ring-ring h-8 w-12 rounded-md border px-1 text-center font-medium focus:border-transparent focus:ring-2 focus:outline-none"
          min={min}
          max={max}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full"
          disabled={value >= max}
          onClick={() => {
            if (value === 0) {
              // When increasing from 0, jump to minTicketBooking
              onChange(Math.min(max, minTicketBooking));
            } else {
              onChange(Math.min(max, value + 1));
            }
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

const RateServicesSkeleton = () => {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
};

const ActionsSkeleton = () => {
  return (
    <div className="flex items-center justify-between gap-2">
      <Skeleton className="h-10 w-1/4" />
      <Skeleton className="h-10 w-1/4" />
    </div>
  );
};

const HeaderSkeleton = () => {
  return (
    <div className="space-y-2">
      <Skeleton className="h-10 w-3/4" />
      <Skeleton className="h-6 w-1/2" />
    </div>
  );
};
